import * as React from 'react';
import { useEffect, useMemo } from 'react';

import { VWOTag, WESAskCoursera, shouldShowWESAskCoursera } from '@coursera-web/wes-common';
import { compose } from 'recompose';

import connectToStores from 'js/lib/connectToStores';
import Retracked from 'js/lib/retracked';
import { useLocation, useNavigate } from 'js/lib/useRouter';

// The "Need to pay by invoice? Chat with our sales team" link is removed by https://coursera.atlassian.net/browse/HP-96
// See "useEffect()" for details
// import insertDriftChatBot from 'bundles/growth-upsell/drift/driftChat';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';
import TeamsContainer from 'bundles/teams-v2/components/TeamsContainer';
import TeamsMainApp from 'bundles/teams-v2/components/TeamsMainApp';
import { getTeamsCheckoutRotatingQuestions, getTeamsCoachQuickPrompts } from 'bundles/teams-v2/constants/coachPrompts';
import { BusinessEmailValidationProvider } from 'bundles/teams-v2/contexts/BusinessEmailValidationContext';
import { isPurchaseAccountPage } from 'bundles/teams-v2/utils/pathnameUtils';

import _t from 'i18n!nls/teams-v2';

import 'css!../styl/index';

type Props = {
  children?: React.ReactNode;
};

const TeamsApp = ({ children }: Props) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const showCoach = shouldShowWESAskCoursera(pathname);
  const isOnPurchaseAccountPage = isPurchaseAccountPage(pathname);

  useEffect(() => {
    if (pathname === '/purchase') {
      navigate(`/purchase/plan`, { replace: true });
    }
  }, [pathname, navigate]);

  const quickPrompts = useMemo(getTeamsCoachQuickPrompts, []);
  const rotatingQuestions = getTeamsCheckoutRotatingQuestions();
  const welcomeMessage = _t('How can I help?');
  const pageContext = useMemo(
    () => [
      { contextKey: 'additionalContext.currentPage', contextValue: pathname },
      { contextKey: 'additionalContext.product', contextValue: 'teams' },
    ],
    [pathname]
  );

  return (
    <>
      <TeamsContainer>
        <VWOTag />
        <BusinessEmailValidationProvider>
          <TeamsMainApp>{children}</TeamsMainApp>
        </BusinessEmailValidationProvider>
      </TeamsContainer>
      <WESAskCoursera
        key="teams-wes-ask-coursera"
        disabled={!showCoach}
        launcherPlacement={isOnPurchaseAccountPage ? 'header' : 'floating'}
        position="bottom-right"
        quickPrompts={quickPrompts}
        rotatingQuestions={rotatingQuestions}
        welcomeMessage={welcomeMessage}
        pageContext={pageContext}
      />
    </>
  );
};

export default compose<Props, {}>(
  Retracked.createTrackedContainer(() => {
    return {
      namespace: {
        app: 'teams_v2',
        page: 'teams_v2_page',
      },
    };
  }),
  connectToStores<Props, {}>([ApplicationStoreClass], (ApplicationStore, props) => ({
    ...props,
    csrfToken: ApplicationStore.getState().csrfToken,
    requestCountryCode: ApplicationStore.getState().requestCountryCode,
  }))
)(TeamsApp);
